import React, { useMemo } from 'react';
import { isEmpty, keyBy } from 'lodash';
import Collapsible from '../../../../../Components/Base/Collapsible';
import DataTable from '../../../../../Components/Base/DataTable';
import TableTrigger from '../../../../../Components/Base/DataTable/Trigger';
import Loading from '../../../../../Components/Loading';
import {
	useInitialQuestionnaireResultQuery,
	useQuestionnaireResultItemsQuery,
} from '../../../../../graphql/graphql';
import { getDateDifference, getDateDifferenceMoment } from '../utils/date';
import useTranslation from '../../../../../Hooks/useTranslation';
import { getMomentDate } from '../../../../../Utils/dates.util';
//@ts-ignore
import styles from './MothersHealthSection.module.scss';
import { Moment } from 'moment';

const DATE_FORMAT = 'DD.MM.yyyy';

type Translation = (
	key: string | undefined,
	params?: any,
) => string | undefined;

const getGynecologicalAnamnesisDetails = (
	t: Translation,
	childBirthDate?: Moment,
) => [
	{
		key: 'currentPregnancy.plannedDateOfBirth',
		label: 'base.actualGestationalWeek',
		path: 'rawValue',
		callback: (dateField: string) => {
			const date = getMomentDate(dateField);
			const { weeks, days } = getDateDifferenceMoment({
				dateOfBirth: childBirthDate,
				estimatedDateOfBirth: date,
			});

			return `${weeks} + ${days}`;
		},
	},
	{
		key: 'currentPregnancy.currentPregnancyMethod',
		label: 'base.currentPregnancy.method',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'currentPregnancy.plannedDateOfBirth',
		label: 'base.plannedDateOfBirth',
		path: 'rawValue',
		callback: (date: string) => getMomentDate(date).format(DATE_FORMAT),
	},
	{
		key: 'mothersHealth.weighBeforePregnancy',
		label: 'base.weighBeforePregnancy',
		path: 'rawValue',
		callback: (value: string) => `${value} Kg`,
	},
	{
		key: 'finalStep.careDate',
		label: 'base.careDate',
		path: 'rawValue',
		additionalKeys: ['currentPregnancy.plannedDateOfBirth'],
		callback: (dateField: string, additionalKeys: string[] = []) => {
			const [plannedDateOfBirth] = additionalKeys;

			const date = getMomentDate(dateField);
			const { weeks, days } = getDateDifferenceMoment({
				dateOfBirth: date,
				estimatedDateOfBirth: getMomentDate(plannedDateOfBirth),
			});

			return `${date.format(DATE_FORMAT)} (${weeks} + ${days})`;
		},
	},
	{
		key: 'currentPregnancy.totalNumberOfPregnanciesIncludingCurrent',
		label: 'base.totalNumberOfPregnanciesIncludingCurrent',
		path: 'rawValue',
	},
	{
		key: 'previousPregnancies',
		label: 'base.totalNumberOfBirths',
		path: 'rawValue',
		callback: (previousPregnancies: any[]) => {
			const birthsCount =
				previousPregnancies?.reduce(
					(acc, { details }) => (details.type === 'birth' ? acc + 1 : acc),
					0,
				) ?? 0;

			return `${birthsCount}`;
		},
	},
	{
		key: 'previousPregnancies',
		label: 'base.totalNumberOfAbortion',
		path: 'rawValue',
		callback: (previousPregnancies: any[]) => {
			const abortionsCount =
				previousPregnancies?.reduce(
					(acc, { details }) => (details.type === 'abortion' ? acc + 1 : acc),
					0,
				) ?? 0;

			return `${abortionsCount}`;
		},
	},
	{
		key: 'previousPregnancies',
		label: 'base.totalNumberOfOther',
		path: 'rawValue',
		callback: (previousPregnancies: any[]) => {
			const abortionsCount =
				previousPregnancies?.reduce(
					(acc, { details }) => (details.type === 'other' ? acc + 1 : acc),
					0,
				) ?? 0;

			return `${abortionsCount}`;
		},
	},
];

const getLabResultDetails = (t: Translation) => [
	{
		key: 'labResults.bloodGroup.type.value',
		label: 'base.bloodGroup.type',
		path: 'rawValue',
	},
	{
		key: 'labResults.bloodGroup.factor.value',
		label: 'base.bloodGroup.factor',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
		renderChildren: function ({ fieldsKeys }: { fieldsKeys: any }) {
			const NESTED_TABLE_DETAILS = [
				{
					key: 'labResults.bloodGroup.antiD.applicationOfAntiD',
					label: 'base.bloodGroup.antiD.title',
					path: 'rawValue',
					callback: (value: string) =>
						value ? t(`base.${value}`)?.toUpperCase() : '-',
				},
				{
					key: 'labResults.bloodGroup.antiD.date',
					label: 'base.bloodGroup.antiD.date',
					path: 'rawValue',
					callback: (dateField: string) => {
						if (!dateField) {
							return '-';
						}

						const date = getMomentDate(dateField);

						return date.format(DATE_FORMAT);
					},
				},
				{
					key: 'labResults.bloodGroup.antiD.medicineName.item.name',
					label: 'base.bloodGroup.antiD.medicineName',
					path: 'rawValue',
					callback: (value: string) => {
						const addition =
							fieldsKeys[
								'labResults.bloodGroup.antiD.medicineName.item.addition'
							];
						let val = value ?? ' - ';
						if (addition?.valueJson?.rawValue) {
							val = val + ', ' + addition?.valueJson?.rawValue;
						}
						return val;
					},
				},
				{
					key: 'labResults.bloodGroup.antiD.dose',
					label: 'base.bloodGroup.antiD.dose',
					path: 'rawValue',
					callback: (value: string) => value ?? '-',
				},
			]
				.map(({ callback, key, path, ...row }) => ({
					...row,
					value: callback
						? callback(fieldsKeys[key]?.valueJson[path])
						: fieldsKeys[key]?.valueJson[path],
				}))
				.filter(({ value }) => value);

			return (
				<div className='mt-2'>
					<DataTable>
						{NESTED_TABLE_DETAILS.map(({ value, label }) => (
							<DataTable.Row key={label} label={label} value={value} isNested />
						))}
					</DataTable>
				</div>
			);
		},
	},
	{
		key: 'labResults.hbsag',
		label: 'base.hbsag',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.hepc',
		label: 'base.hepc',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.hiv',
		label: 'base.hiv',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.bwr',
		label: 'base.bwr',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.antibodies',
		label: 'base.antibodies',
		path: 'rawValue',
		callback: (value: string) => t(`base.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.amcCvsNipt',
		label: 'base.amcCvsNipt',
		path: 'rawValue',
		callback: (value: string) => t(`base.labResults.${value}`)?.toUpperCase(),
	},
	{
		key: 'labResults.gbs',
		label: 'base.gbs',
		path: 'rawValue',
		secondaryKeyValue: 'labResults.gbsSwapDate',
		callback: (value: string, swapDateField: any) => {
			const swapDate = swapDateField
				? ` (${getMomentDate(swapDateField.valueJson.rawValue).format(
						DATE_FORMAT,
				  )})`
				: '';

			return `${t(`base.${value}`)?.toUpperCase()}${swapDate}`;
		},
	},
	{
		key: 'labResults.hemoglobinDetails',
		label: 'base.hemoglobin.title',
		path: 'rawValue',
		callback: (details: any[]) => {
			const items = details.map(({ collectionDate, value }) => (
				<div className='flex-column justify-content-center'>
					<p className={styles.date}>
						{getMomentDate(collectionDate).format(DATE_FORMAT)}
					</p>
					<p className={styles.value}>{`${value} g/l`}</p>
				</div>
			));

			return <div className='ml-2'>{items}</div>;
		},
	},
	{
		key: 'labResults.plateletsDetails',
		label: 'base.platelets.title',
		path: 'rawValue',
		callback: (details: any[]) => {
			const items = details.map(({ collectionDate, value }) => (
				<div className='flex-column justify-content-center'>
					<p className={styles.date}>
						{getMomentDate(collectionDate).format(DATE_FORMAT)}
					</p>
					<p className={styles.value}>
						{value} x10
						<sup>9</sup>/l
					</p>
				</div>
			));

			return <div className='ml-2'>{items}</div>;
		},
	},
];

interface Props {
	patientId: string;
	healthPlanCode?: string | null;
}

const MothersHealthSection: React.FC<Props> = ({
	patientId,
	healthPlanCode,
}) => {
	const { t } = useTranslation();
	const { data, isLoading } = useInitialQuestionnaireResultQuery({
		userId: patientId,
		healthPlanCode,
	});

	const endQuestionnaire = useQuestionnaireResultItemsQuery({
		questionnaireCode: 'meddibaby_end_questionnaire',
		patientId,
		take: 9999,
	});

	const childBirthDate = useMemo(() => {
		const value = endQuestionnaire?.data?.questionnaireResultItems?.data?.find(
			item => item?.question?.key === 'birthDate',
		)?.valueJson?.rawValue;
		if (value) {
			return getMomentDate(value);
		}
		return undefined;
	}, [endQuestionnaire]);

	const { initialQuestionnaireResult } = data?.healthPlanUser ?? {};

	const rows = initialQuestionnaireResult?.questionnaireResultItems ?? [];

	const fieldsKeys = keyBy(rows, 'question.key');

	const GYNECOLOGICAL_ANAMNESIS_DATA = useMemo(() => {
		if (isEmpty(fieldsKeys)) {
			return [];
		}

		return getGynecologicalAnamnesisDetails(t, childBirthDate)
			.map(({ callback, key, additionalKeys = [], path, ...row }) => ({
				...row,
				value: callback
					? callback(
							fieldsKeys[key]?.valueJson[path],
							additionalKeys.length > 0
								? additionalKeys.map(
										additionalKey => fieldsKeys[additionalKey]?.valueJson[path],
								  )
								: [],
					  )
					: fieldsKeys[key]?.valueJson[path],
			}))
			.filter(({ value }) => value);
	}, [fieldsKeys]);

	const LAB_RESULTS_DATA = useMemo(() => {
		if (isEmpty(fieldsKeys)) {
			return [];
		}

		return getLabResultDetails(t)
			.map(({ callback, key, path, secondaryKeyValue, ...row }) => ({
				...row,
				value: callback
					? callback(
							fieldsKeys[key]?.valueJson[path],
							secondaryKeyValue ? fieldsKeys[secondaryKeyValue] : undefined,
					  )
					: fieldsKeys[key]?.valueJson[path],
			}))
			.filter(({ value }) => value);
	}, [fieldsKeys]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className='overflow-auto'>
			<Collapsible
				open
				trigger={({ isExpanded }) => (
					<TableTrigger
						isExpanded={isExpanded}
						title='base.gynecologicalAnamnesis'
					/>
				)}>
				<DataTable>
					{GYNECOLOGICAL_ANAMNESIS_DATA.map(({ value, label }) => (
						<DataTable.Row key={label} label={label} value={value} />
					))}
				</DataTable>
			</Collapsible>
			<Collapsible
				open
				trigger={({ isExpanded }) => (
					<TableTrigger
						isExpanded={isExpanded}
						title='base.shared.labAnalysis'
					/>
				)}>
				<DataTable>
					{LAB_RESULTS_DATA.map(({ label, value, renderChildren }) => (
						<DataTable.Row key={label} label={label} value={value}>
							{renderChildren?.({ fieldsKeys })}
						</DataTable.Row>
					))}
				</DataTable>
			</Collapsible>
		</div>
	);
};

export default MothersHealthSection;
